import { keepPreviousData, QueryClient, useInfiniteQuery, useMutation, useQuery, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import { ResponseType } from 'axios';
import { del, delAuth, get, getAuth, patchAuth, post, postAuth, put, putAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';

// Type helper for mutation options (excludes mutationFn and mutationKey which are set internally)
type MutationOptions<TData, TError, TVariables> = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn' | 'mutationKey'>;

/** Per-query overrides. The url and the fetch stay with the hook. */
type QueryOverrides<TData> = Omit<UseQueryOptions<TData, Error>, 'queryKey' | 'queryFn'>;

// Constants
const PAGE_SIZE = 50;

export interface PaginatedResponse<T> {
  data: T[];
  filteredRecords: number;
  statusCounts?: Record<string, number>;
  classificationCounts?: Record<string, number>;
  expirationCounts?: Record<string, number>;
}

export const useGetApi = <T>(url: string, params?: object) => {
  return useQuery<T, Error>({
    queryKey: [url, (params: object) => JSON.stringify(params)],
    queryFn: ({ signal }) => get<T>(url, { ...params, signal }),
    // An empty url means "nothing to load", as in useGetAuthApi. Fetched anyway it asked for the
    // bare /api/, which the server answers with the old site's index.html - and that page read
    // the HTML as a saved open-fund draft and told every new visitor they had one.
    enabled: !!url,
  });
};

export const useGetAuthApi = <T>(
  url: string,
  responseType?: ResponseType,
  /**
   * Overrides for this one query. Optional, so every existing caller keeps the defaults -
   * added for endpoints that must not be retried, such as the ones behind a provider's
   * hourly call limit, where the global retry: 2 turns one failure into three calls.
   */
  overrides?: QueryOverrides<T>,
) => {
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };
  return useQuery<T, Error>({
    queryKey: authQueryKey(url),
    queryFn: ({ signal }) => getAuth<T>(url, headers, responseType, signal),
    enabled: !!url,
    staleTime: 0,
    ...overrides,
  });
};

/** The cache key useGetAuthApi files a url under - shared so a prefetch lands where the hook reads. */
export const authQueryKey = (url: string) => [url, (params: object) => JSON.stringify(params)];

/**
 * Starts the same fetch useGetAuthApi would make for `url`, before the screen that reads it has
 * mounted. When the screen's hook mounts it joins the request in flight, or reads the answer.
 *
 * Give the screen's query the same staleTime: with the default of 0, a prefetch that finished
 * before the screen mounted is fetched a second time the moment it does.
 *
 * The fetch deliberately ignores the abort signal. React Query aborts a query that reads it as soon
 * as its last observer leaves - and a prefetch starts with none, so a screen that mounts and
 * unmounts once (StrictMode does exactly that) threw the request away and sent it again.
 */
export const prefetchAuthApi = <T>(
  queryClient: QueryClient,
  url: string,
  token: string,
  options: Pick<QueryOverrides<T>, 'staleTime' | 'meta'> = {},
) =>
  queryClient.prefetchQuery<T, Error>({
    queryKey: authQueryKey(url),
    queryFn: () => getAuth<T>(url, { Authorization: `Bearer ${token}` }),
    ...options,
  });

export const usePostApi = <T, P = object>(url: string, options?: MutationOptions<T, Error, P>) => {
  return useMutation<T, Error, P>({
    mutationFn: (body: P) => post<T, P>(url, body),
    mutationKey: [url, (body: P) => JSON.stringify(body)],
    ...options,
  });
};

export const usePutApi = <T, P = object>(url: string, options?: MutationOptions<T, Error, P>) => {
  return useMutation<T, Error, P>({
    mutationFn: (body: P) => put<T, P>(url, body),
    mutationKey: [url, (body: P) => JSON.stringify(body)],
    ...options,
  });
};

export const usePutAuthApi = <T, P = object, B = object>(url: string, options?: MutationOptions<T, Error, { params?: P; body?: B }>) => {
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };
  return useMutation<T, Error, { params?: P; body?: B }>({
    mutationFn: ({ params = {} as P, body = {} as B }) => putAuth<T, P, B>(url, headers, params, body),
    mutationKey: [url, (params?: P) => (params ? JSON.stringify(params) : 'default')],
    ...options,
  });
};

export const usePostAuthApi = <T, P = object>(url: string, options?: MutationOptions<T, Error, P>) => {
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };
  return useMutation<T, Error, P>({
    mutationFn: (body: P) => postAuth<T, P>(url, headers, body),
    mutationKey: [url],
    ...options,
  });
};

/**
 * Hook for POST requests that behave like GET queries (e.g., pagination with filters).
 * Re-fetches when the body changes.
 */
export const usePostAuthApiQuery = <T, P = object>(
  url: string,
  body: P,
  /** Overrides for this one query (e.g. retry: false). Optional, so existing callers keep the defaults. */
  overrides?: QueryOverrides<T>,
) => {
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };
  return useQuery<T, Error>({
    queryKey: [url, JSON.stringify(body)],
    queryFn: ({ signal }) => postAuth<T, P>(url, headers, body, signal),
    enabled: !!url && !!currentUser?.token,
    ...overrides,
  });
};

export const usePostAuthApiInfiniteQuery = <T, P = object>(url: string, body: P, pageSize?: number) => {
  const { currentUser } = useAuthContext();
  return useInfiniteQuery<PaginatedResponse<T>>({
    queryKey: [url, JSON.stringify(body), pageSize],
    queryFn: async ({ pageParam, signal }) => {
      const headers = { Authorization: `Bearer ${currentUser?.token}` };
      const data = await postAuth<PaginatedResponse<T>, P>(
        url,
        headers,
        {
          start: pageParam,
          length: pageSize ?? PAGE_SIZE,
          ...body,
        },
        signal
      );
      return data;
    },
    enabled: !!url && !!currentUser?.token,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((sum, page) => sum + page.data.length, 0);
      return totalFetched < lastPage.filteredRecords ? totalFetched : undefined;
    },
    placeholderData: keepPreviousData,
  });
};

export const usePatchAuthApi = <T, P = object>(url: string, options?: MutationOptions<T, Error, P>) => {
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };
  return useMutation<T, Error, P>({
    mutationFn: (body: P) => patchAuth<T, P>(url, body, headers),
    mutationKey: [url],
    ...options,
  });
};

export const useDeleteApi = <T>(url: string, params?: object, options?: MutationOptions<T, Error, void>) => {
  return useMutation<T, Error, void>({
    mutationFn: () => del<T>(url, params),
    mutationKey: [url, (params: object) => JSON.stringify(params)],
    ...options,
  });
};

export const useDeleteAuthApi = <T>(url: string, options?: MutationOptions<T, Error, void>) => {
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };
  return useMutation<T, Error, void>({
    mutationFn: () => delAuth<T>(url, headers),
    mutationKey: [url],
    ...options,
  });
};
